import { Content } from "@builder.io/sdk-react";

import { makeChromeBlockGuard } from "./chrome-block-guard";
import { GuardedBuilderLink } from "./guarded-builder-link";
import {
  VISIBLE_CHROME_REGISTERED_NAMES,
  visibleChromeCustomComponents,
} from "./visible-chrome-registry";

type ContentProps = React.ComponentProps<typeof Content>;

// Lazy-initialized at first render, NOT at module eval: this module sits on a
// module cycle (visible-chrome-registry → LMN gate blocks → routes/layout →
// builder-footer → this module), so a top-level cross-module call can run
// before chrome-block-guard has evaluated. By render time every module has.
let warnUnknownVisibleChromeBlocks:
  | ReturnType<typeof makeChromeBlockGuard>
  | undefined;

/**
 * `Content` for the always-VISIBLE chrome surfaces — footer, alert bar,
 * SOW/affiliate nav bar. These render synchronously on every page, so they
 * bind only the audited visible subset (COM-189 wave 4C); the overlay-only
 * block definitions (account dropdown, mobile nav) ride the lazy overlay
 * chunks via `ChromeContent` instead.
 *
 * SSR and the client render the SAME registry for a given surface, so a block
 * outside the subset renders blank identically on both — never a hydration
 * mismatch — and the guard below plus the per-model fixture test
 * (tests/unit/hec/builder/chrome-registry.test.ts) make it one log line /
 * one red test, not a silent regression.
 */
export function VisibleChromeContent(
  // `enrich` is excluded at the type level so a caller can't smuggle it in via
  // a props object (COM-586 — see docs/working-conventions.md §10).
  props: Omit<ContentProps, "customComponents" | "enrich">,
): React.JSX.Element {
  // Render-time on purpose: runs during SSR too, so a chrome entry that
  // references an unknown block shows up in Worker logs, not only in the
  // browser console.
  warnUnknownVisibleChromeBlocks ??= makeChromeBlockGuard(
    VISIBLE_CHROME_REGISTERED_NAMES,
    "the visible-chrome registry (app/hec/builder/visible-chrome-registry.ts)",
  );
  warnUnknownVisibleChromeBlocks(props.content, props.model);
  return (
    <Content
      {...props}
      customComponents={visibleChromeCustomComponents}
      linkComponent={GuardedBuilderLink}
    />
  );
}
